import {getLocale} from './LocaleSupport';
import IntlMessageFormat from 'intl-messageformat';
import {getEnvironment} from '../environment';

/**
 * Functions for formatting localized messages.
 */

export interface KeyValueMap {
   [key: string]: string
}

export interface LocaleMap {
   [localeKey: string]: KeyValueMap
}

let defaultMessages: LocaleMap = {};

/**
 * Sets the messages map to be used by the formatMessage function.
 * @param {LocaleMap} messages Map of locales with a map of messages for each locale.
 */
export const initializeMessages = (messages: LocaleMap) => {
   if (!messages['en']) {
      throw new Error(`messages must have an entry for 'en'`);
   }

   defaultMessages = messages;
};

export type FormatMessageValuesType = Record<string, string> | Array<string> | undefined;

/**
 * Looks up the key in the messages map, replacing any substitution parameters in the message text with the
 * specified values.
 *
 * If the key is not found then ???key??? will be returned.
 * @param {string} key The message key to locate
 * @param {Array<string>} values Optional values used to fill in paramters in the message text
 * @returns {string} The text to be displayed.
 */
export const formatMessage = (key: string, values?: FormatMessageValuesType) => {
   const resourceOverrides = getEnvironment().resourceOverrides;

   if (resourceOverrides) {
      const result = formatMessageBundle(resourceOverrides, key, values);

      if (result !== `???${key}???`) {
         return result;
      }
   }

   return formatMessageBundle(defaultMessages, key, values);
};

/**
 * Looks up the key in the messages map, replacing any substitution parameters in the message text with the
 * specified values.
 *
 * If the key is not found then ???key??? will be returned.
 * @param {LocaleMap} messages The message bundle used to get messages
 * @param {string} key The message key to locate
 * @param {Array<string>} values Optional values used to fill in paramters in the message text
 * @returns {string} The text to be displayed.
 */
export const formatMessageBundle = (messages: LocaleMap, key: string, values?: FormatMessageValuesType):string => {
   const locale = getLocale();
   const message = getMessageForLocaleAndKey(messages, locale, key);

   if (message) {
      const recordValues = Array.isArray(values) ? values.map((value, index) => ({
         [''+index]: value
      })).reduce((previousValue, currentValue) => ({
         ...previousValue,
         ...currentValue
      })) : values;
      return new IntlMessageFormat(message, locale, undefined, { ignoreTag: true }).format(recordValues) as string;
   }

   return `???${key}???`;
};

const getMessageForLocaleAndKey = (messages: LocaleMap, locale: string, key: string): string | null => {
   let message = lookupMessageForLocaleAndKey(messages, locale, key);

   if (message) {
      return message;
   }

   if (locale.indexOf('-') > 0) {
      message = lookupMessageForLocaleAndKey(messages, locale.split('-')[0], key);

      if (message) {
         return message;
      }
   }

   return lookupMessageForLocaleAndKey(messages, 'en', key);
};

const lookupMessageForLocaleAndKey = (messages: LocaleMap, locale: string, key: string): string | null => {
   const messagesForLocale = messages[locale];

   return messagesForLocale ? messagesForLocale[key] : null;
};
